import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, LineChart } from "lucide-react";

import { useAccounts } from "../api/hooks/useAccounts";
import { useAddLeg } from "../api/hooks/useAddLeg";
import { useDeleteTradeLeg } from "../api/hooks/useDeleteTradeLeg";
import { useInstruments } from "../api/hooks/useInstruments";
import { useStrategies } from "../api/hooks/useStrategies";
import { useRecordFill } from "../api/hooks/useRecordFill";
import { useTrade } from "../api/hooks/useTrade";
import { useTradeChart } from "../api/hooks/useTradeChart";
import type { components } from "../api/schema";
import { AttachmentGallery } from "../components/attachments/AttachmentGallery";
import { CandlestickChart } from "../components/trades/CandlestickChart";
import { TradeNotesPanel } from "../components/trades/TradeNotesPanel";
import {
  DirectionBadge,
  OutcomeBadge,
  StatusBadge,
  SymbolAvatar,
} from "../components/trades/tradeCells";
import { TradeTimeline } from "../components/trades/TradeTimeline";
import { pnlClassName } from "../components/trades/tradeColumns";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatDateTime, formatTime } from "../lib/date";
import { markTradesRestore, readTradesSnapshot } from "../lib/tradesListState";
import { formatMoney, formatPrice, formatSignedMoney, sumDecimals } from "../lib/money";
import {
  FILL_ACTION_EXECUTION_LABELS,
  formatHoldDuration,
  formatPercent,
  formatQuantity,
  formatRMultiple,
} from "../lib/tradeFormat";

type TradeDetailParams = {
  tradeId: string;
};

type TradeDetailSearch = {
  from?: "trades" | "dashboard" | "alerts";
};

type LegResponse = components["schemas"]["LegResponse"];
type LegDirection = components["schemas"]["LegDirection"];
type FillAction = components["schemas"]["FillAction"];
type FillResponse = components["schemas"]["FillResponse"];

const DIRECTIONS: readonly LegDirection[] = ["long", "short"];
const FILL_ACTIONS: readonly FillAction[] = ["bto", "sto", "btc", "stc"];
const EM_DASH = "—";

function titleCase(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

export function TradeDetailPage() {
  const { tradeId } = useParams({ strict: false }) as TradeDetailParams;
  const { from } = useSearch({ strict: false }) as TradeDetailSearch;
  const { data: trade, isLoading, isError, error } = useTrade(tradeId);
  const { data: instrumentsData } = useInstruments();
  const [editing, setEditing] = useState(false);
  // Restore the trades list exactly as the user left it: the saved URL search
  // (page / sort / origin) goes onto the back link, and marking the restore flag
  // tells the Trades page to re-open the same session days. Read once on mount.
  const [tradesBackSearch] = useState(() => readTradesSnapshot()?.search ?? {});

  // Always open the detail page at the top -- the shell's scroll container keeps
  // its position across route changes, so a deep scroll on the list (or a prior
  // trade) would otherwise carry over and hide the header.
  useEffect(() => {
    const main = document.querySelector('[data-scroll-restoration-id="app-main"]');
    if (main instanceof HTMLElement) main.scrollTop = 0;
  }, [tradeId]);
  const symbolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const instrument of instrumentsData?.items ?? []) {
      map.set(instrument.instrument_id, instrument.symbol);
    }
    return map;
  }, [instrumentsData]);

  if (isLoading) {
    return (
      <section className="p-6" role="status" aria-live="polite">
        <p className="text-on-surface-variant">Loading trade…</p>
      </section>
    );
  }

  if (isError || trade === undefined) {
    return (
      <section className="p-6" role="alert">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Trade not found</h1>
        <p className="mt-2 text-on-surface-variant">
          {error instanceof Error ? error.message : "The trade could not be loaded."}
        </p>
        <Link to="/trades" className="mt-4 inline-block text-primary underline">
          Back to trades
        </Link>
      </section>
    );
  }

  // The domain rejects structural mutations on a soft-deleted or fully
  // closed trade (Trade._guard_mutable); the editor affordances mirror
  // that guard so single-leg / closed trades render read-only (zero
  // regression from the pre-M10-U2 view).
  const editable = !trade.is_deleted && trade.status.toLowerCase() !== "closed";

  // First leg's instrument symbol drives the heading (ADR 0022). Falls back
  // to "Trade detail" while instruments load or for a legless trade.
  const headingSymbol = symbolById.get(trade.legs[0]?.instrument_id ?? "");

  // Every fill across every leg, the source for the Chart tab. The intraday
  // candlestick chart (massive.com bars, ADR 0033 U4/U5) supersedes this
  // execution-price line; until then it gives a real, data-backed chart.
  const allFills = trade.legs.flatMap((leg) => leg.fills);

  const metaLine = [
    trade.opened_at ? `Opened ${formatDateTime(trade.opened_at)}` : null,
    trade.closed_at ? `Closed ${formatDateTime(trade.closed_at)}` : null,
    trade.hold_duration_seconds === null || trade.hold_duration_seconds === undefined
      ? null
      : `Held ${formatHoldDuration(trade.hold_duration_seconds)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  const backClass =
    "inline-flex items-center gap-1.5 rounded-control border border-outline bg-surface-container px-3 py-1.5 text-sm font-medium text-on-surface shadow-sm transition-colors hover:border-primary hover:bg-surface-container-high hover:text-primary";

  // The back button returns to wherever the trade was opened from. Dashboard
  // and Alerts are plain destinations; the Trades list additionally restores
  // the saved list state (page / sort / expanded session days).
  const backLink =
    from === "dashboard" ? (
      <Link to="/dashboard" aria-label="Back to dashboard" className={backClass}>
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        Back to dashboard
      </Link>
    ) : from === "alerts" ? (
      <Link to="/alerts" aria-label="Back to alerts" className={backClass}>
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        Back to alerts
      </Link>
    ) : (
      <Link
        to="/trades"
        search={tradesBackSearch}
        aria-label="Back to trades"
        onClick={() => markTradesRestore()}
        className={backClass}
      >
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        Back to trades
      </Link>
    );

  return (
    <section className="p-6 space-y-6">
      {backLink}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {headingSymbol !== undefined ? <SymbolAvatar symbol={headingSymbol} /> : null}
            <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
              {headingSymbol ?? "Trade detail"}
            </h1>
            <DirectionBadge direction={trade.legs[0]?.direction} />
            <StatusBadge status={trade.status} />
            <OutcomeBadge status={trade.status} realizedPnl={trade.realized_pnl_amount} />
          </div>
          <p className="mt-1 font-data text-xs text-on-surface-muted">
            {metaLine === "" ? "Not yet opened" : metaLine}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/trades/$tradeId/analytics"
            params={{ tradeId: trade.trade_id }}
            className="inline-flex items-center gap-1.5 text-primary hover:underline"
          >
            <LineChart aria-hidden="true" className="h-4 w-4" />
            Analytics
          </Link>
        </div>
      </div>

      <KpiRibbon trade={trade} executionCount={allFills.length} />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* Left column: the chart, the execution ledger, then the legs
            breakdown and screenshots side by side beneath it. */}
        <div className="min-w-0 space-y-6">
          <RightPanel trade={trade} fills={allFills} />
          <ExecutionsTable trade={trade} />
          <div className="grid items-start gap-6 md:grid-cols-2">
            <AttachmentGallery tradeId={trade.trade_id} />
            <LegsCard trade={trade} editable={editable && editing} />
          </div>
        </div>

        {/* Right rail: the lifecycle and the standing facts. */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
              <CardDescription>
                Lifecycle of this trade, open to close.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TradeTimeline trade={trade} />
            </CardContent>
          </Card>

          <TradeDetailsCard
            trade={trade}
            editable={editable}
            editing={editing}
            onToggleEdit={() => setEditing((value) => !value)}
          />
        </div>
      </div>

      {editable && editing ? (
        <div className="grid gap-6 md:grid-cols-2">
          <AddLegForm tradeId={trade.trade_id} />
          {trade.legs.length > 0 ? (
            <RecordFillForm tradeId={trade.trade_id} legs={trade.legs} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// At-a-glance metrics strip across the top of the detail page (mockup
// `.dribbon`): six server-computed KPIs in a single bordered rail. Net P&L,
// return and R take the diverging P&L colour; the rest stay neutral. All values
// come straight off TradeResponse, so they reconcile with the rail and grid.
function KpiRibbon({
  trade,
  executionCount,
}: {
  trade: components["schemas"]["TradeResponse"];
  executionCount: number;
}) {
  const { currency } = trade;
  const pnlClass = pnlClassName(trade.realized_pnl_amount);
  const cells: { label: string; value: string; className?: string }[] = [
    {
      label: "Net P&L",
      value: formatSignedMoney(trade.realized_pnl_amount, { currency }),
      className: pnlClass,
    },
    {
      label: "Return",
      value: formatPercent(trade.realized_return_pct),
      className: pnlClassName(trade.realized_return_pct),
    },
    {
      label: "R-multiple",
      value: formatRMultiple(trade.r_multiple),
      className: pnlClass,
    },
    { label: "Hold", value: formatHoldDuration(trade.hold_duration_seconds) },
    { label: "Fees", value: formatMoney(trade.fees_amount, { currency }) },
    { label: "Executions", value: executionCount.toString() },
  ];

  return (
    <dl className="grid grid-cols-2 overflow-hidden rounded-card border border-outline-variant bg-surface-container sm:grid-cols-3 lg:grid-cols-6">
      {cells.map((cell) => (
        <div
          key={cell.label}
          className="flex flex-col gap-1.5 border-b border-r border-outline-variant px-4 py-3.5"
        >
          <dt className="text-xs text-on-surface-muted">{cell.label}</dt>
          <dd
            className={`font-data text-[22px] font-semibold leading-none tabular-nums ${
              cell.className ?? "text-on-surface"
            }`}
          >
            {cell.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Trade-detail Chart tab height. Taller than the compact preview chart so the
// intraday candles read clearly in the wide right column rather than as a
// letterbox strip.
const DETAIL_CHART_HEIGHT_PX = 420;

type RightTab = "chart" | "notes";

// The right column of the detail header: a Chart / Notes tab switcher. Chart
// shows the intraday candlestick chart (massive.com OHLC bars via the chart
// endpoint) with the trade's executions overlaid as markers; Notes shows the
// trade's free-text journal. The bars request is deferred until the Chart tab
// is shown.
function RightPanel({
  trade,
  fills,
}: {
  trade: components["schemas"]["TradeResponse"];
  fills: FillResponse[];
}) {
  const [tab, setTab] = useState<RightTab>("chart");
  const chartQuery = useTradeChart(trade.trade_id, "5m", tab === "chart");
  const bars = chartQuery.data?.bars ?? [];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-outline-variant" role="tablist">
        <TabButton active={tab === "chart"} onClick={() => setTab("chart")}>
          Chart
        </TabButton>
        <TabButton active={tab === "notes"} onClick={() => setTab("notes")}>
          Notes
        </TabButton>
      </div>

      {tab === "chart" ? (
        <Card>
          <CardContent className="pt-6">
            {chartQuery.isLoading ? (
              <p
                className="flex items-center text-sm text-on-surface-variant"
                style={{ height: DETAIL_CHART_HEIGHT_PX }}
                role="status"
              >
                Loading chart…
              </p>
            ) : (
              <CandlestickChart bars={bars} fills={fills} height={DETAIL_CHART_HEIGHT_PX} />
            )}
            {chartQuery.data?.symbol != null && bars.length > 0 ? (
              <p className="mt-2 text-xs text-on-surface-muted">
                {chartQuery.data.symbol} · intraday {chartQuery.data.resolution} bars
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <TradeNotesPanel trade={trade} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-on-surface"
          : "border-transparent text-on-surface-variant hover:text-on-surface"
      }`}
    >
      {children}
    </button>
  );
}

type ExecutionRow = FillResponse & { value: string };

interface ExecutionSummary {
  rows: ExecutionRow[];
  // Position size: peak absolute net position reached across the sequence (the
  // shares actually at risk in one direction), not the doubled round-trip sum.
  size: number;
  totalFees: number;
}

// Flatten every leg's fills into one chronological, scrollable table so the
// operator sees execution-level detail without leaving the trade-detail page.
// The footer reconciles the run: position size, per-share edge, total fees and
// net P&L -- so the table balances on its own without the stat rail.
function ExecutionsTable({
  trade,
}: {
  trade: components["schemas"]["TradeResponse"];
}) {
  const { rows, size, totalFees } = useMemo<ExecutionSummary>(() => {
    const flattened = trade.legs
      .flatMap((leg) => leg.fills)
      .slice()
      .sort(
        (a, b) =>
          new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime(),
      );

    let running = "0";
    let peak = 0;
    let fees = 0;
    const built = flattened.map((fill) => {
      // Exact running net position (fractional shares never drift), then a
      // Number() only for the peak magnitude that feeds the per-share divisor.
      running = sumDecimals([
        running,
        fill.action.startsWith("b") ? fill.quantity : `-${fill.quantity}`,
      ]);
      peak = Math.max(peak, Math.abs(Number(running)));
      fees += Number(fill.fees_amount);
      const value = (Number(fill.quantity) * Number(fill.price_amount)).toFixed(2);
      return { ...fill, value };
    });
    return { rows: built, size: peak, totalFees: fees };
  }, [trade.legs]);

  const { currency } = trade;
  const netPnl = trade.realized_pnl_amount;
  const perShare = size > 0 ? Number(netPnl) / size : 0;
  const perShareLabel = `${perShare > 0 ? "+" : ""}${formatPrice(perShare.toFixed(4), { currency })}/sh`;
  const pnlClass = pnlClassName(netPnl);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Executions</CardTitle>
        <span className="rounded-full bg-surface-container-low px-2.5 py-0.5 text-xs text-on-surface-muted">
          {rows.length} {rows.length === 1 ? "fill" : "fills"}
        </span>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-on-surface-variant">No executions yet.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto rounded-control border border-outline-variant">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-container-low">
                <tr className="text-[11px] uppercase tracking-wide text-on-surface-muted">
                  <th className="px-4 py-2.5 text-left font-semibold">Time</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Action</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Qty</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Price</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Fee</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Value</th>
                </tr>
              </thead>
              <tbody className="font-data">
                {rows.map((row) => {
                  const buy = row.action.startsWith("b");
                  const hasFee = Number(row.fees_amount) > 0;
                  return (
                    <tr key={row.fill_id} className="border-t border-outline-variant">
                      <td className="px-4 py-2.5 text-on-surface-variant">
                        {formatTime(row.executed_at)}
                      </td>
                      <td className="px-4 py-2.5 font-sans">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            buy
                              ? "bg-success-soft text-success"
                              : "bg-danger-soft text-danger"
                          }`}
                        >
                          {FILL_ACTION_EXECUTION_LABELS[row.action]}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-on-surface">
                        {formatQuantity(row.quantity)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-on-surface">
                        {formatPrice(row.price_amount, { currency: row.price_currency })}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-on-surface-muted">
                        {hasFee
                          ? formatMoney(row.fees_amount, { currency: row.fees_currency })
                          : "–"}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-on-surface">
                        {formatMoney(row.value, { currency })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="font-data">
                <tr className="border-t border-outline bg-surface-container-low font-semibold text-on-surface">
                  <td className="px-4 py-2.5 font-sans">Net</td>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatQuantity(size.toString())}
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${pnlClass}`}>
                    {perShareLabel}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-on-surface-variant">
                    {formatMoney(totalFees.toFixed(2), { currency })}
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${pnlClass}`}>
                    {formatSignedMoney(netPnl, { currency })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Peak absolute net position across the trade's fills -- the share count that
// was actually at risk in one direction. Backs the Trade-details per-share
// edge, matching the size the Executions footer computes the same way.
function peakNetPosition(legs: LegResponse[]): number {
  const fills = legs
    .flatMap((leg) => leg.fills)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime(),
    );
  let running = 0;
  let peak = 0;
  for (const fill of fills) {
    running += fill.action.startsWith("b")
      ? Number(fill.quantity)
      : -Number(fill.quantity);
    peak = Math.max(peak, Math.abs(running));
  }
  return peak;
}

// The trade's standing facts -- side, account, strategy, average prices,
// position size, fees, risk and timestamps -- as the two-column fact grid from
// the v4 mockup (`.dfacts`). Supersedes the removed stat rail; the "Edit"
// action reveals the leg / fill authoring forms at the foot of the page.
function TradeDetailsCard({
  trade,
  editable,
  editing,
  onToggleEdit,
}: {
  trade: components["schemas"]["TradeResponse"];
  editable: boolean;
  editing: boolean;
  onToggleEdit: () => void;
}) {
  const { data: accountsData } = useAccounts();
  const { data: strategiesData } = useStrategies();
  const { currency } = trade;

  const accountName =
    accountsData?.items.find((account) => account.account_id === trade.account_id)
      ?.name ?? EM_DASH;
  const strategyName =
    trade.strategy_id === null
      ? null
      : (strategiesData?.items.find(
          (strategy) => strategy.strategy_id === trade.strategy_id,
        )?.name ?? EM_DASH);

  const size = peakNetPosition(trade.legs);
  const perShare = size > 0 ? Number(trade.realized_pnl_amount) / size : 0;
  const perShareLabel = `${perShare > 0 ? "+" : ""}${formatPrice(perShare.toFixed(4), { currency })}`;
  const pnlClass = pnlClassName(trade.realized_pnl_amount);

  const priceOrDash = (value: string | null | undefined): string =>
    value === null || value === undefined ? EM_DASH : formatPrice(value, { currency });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Trade details</CardTitle>
        {editable ? (
          <button
            type="button"
            onClick={onToggleEdit}
            className="text-sm font-semibold text-primary hover:text-primary-hover"
          >
            {editing ? "Done" : "Edit"}
          </button>
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 border-t border-outline-variant">
          <Fact label="Side">
            <DirectionBadge direction={trade.legs[0]?.direction} />
          </Fact>
          <Fact label="Account" plain>
            {accountName}
          </Fact>
          <Fact label="Strategy" plain>
            {strategyName ?? <AddHint>+ Add strategy</AddHint>}
          </Fact>
          <Fact label="Avg entry">{priceOrDash(trade.average_entry_price)}</Fact>
          <Fact label="Avg exit">{priceOrDash(trade.average_exit_price)}</Fact>
          <Fact label="Edge / share" className={pnlClass}>
            {size > 0 ? perShareLabel : EM_DASH}
          </Fact>
          <Fact label="Position size">
            {formatMoney(trade.opening_notional_amount, { currency })}
          </Fact>
          <Fact label="Fees">{formatMoney(trade.fees_amount, { currency })}</Fact>
          <Fact label="Planned risk">
            {trade.planned_risk_amount === null ||
            trade.planned_risk_amount === undefined ? (
              <AddHint>+ Set risk</AddHint>
            ) : (
              formatMoney(trade.planned_risk_amount, { currency })
            )}
          </Fact>
          <Fact label="Opened" plain>
            {formatTime(trade.opened_at)}
          </Fact>
          <Fact label="Closed" plain>
            {formatTime(trade.closed_at)}
          </Fact>
          <Fact label="Held">
            {formatHoldDuration(trade.hold_duration_seconds)}
          </Fact>
        </div>
      </CardContent>
    </Card>
  );
}

// One label/value cell of the Trade-details grid. `plain` drops the mono
// numeric face for free-text values (account / strategy / timestamps).
function Fact({
  label,
  children,
  plain = false,
  className,
}: {
  label: string;
  children: ReactNode;
  plain?: boolean;
  className?: string;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-outline-variant py-2.5 odd:pr-3.5 even:border-l even:border-outline-variant even:pl-3.5">
      <span className="text-[10.5px] font-medium text-on-surface-muted">{label}</span>
      <span
        className={`text-[13.5px] font-semibold text-on-surface ${plain ? "" : "font-data tabular-nums"} ${className ?? ""}`}
      >
        {children}
      </span>
    </div>
  );
}

// The accent "+ Add …" placeholder shown for an unset strategy / risk. Inert
// for now -- the leg/fill editor lives behind the Trade-details "Edit" toggle.
function AddHint({ children }: { children: ReactNode }) {
  return <span className="font-sans font-medium text-primary">{children}</span>;
}

// Per-leg roll-up card matching the v4 mockup legs block (`.legc` / `.legg`):
// a header with the leg ordinal + side + realised P&L, then a four-cell fact
// grid (Filled / Fills / Avg price / Realised). In edit mode each leg exposes
// its delete affordance.
function LegsCard({
  trade,
  editable,
}: {
  trade: components["schemas"]["TradeResponse"];
  editable: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Legs</CardTitle>
        <span className="rounded-full bg-surface-container-low px-2.5 py-0.5 text-xs text-on-surface-muted">
          {trade.legs.length} {trade.legs.length === 1 ? "leg" : "legs"}
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        {trade.legs.length === 0 ? (
          <p className="text-sm text-on-surface-variant">This trade has no legs.</p>
        ) : (
          trade.legs.map((leg, index) => (
            <LegRow
              key={leg.leg_id}
              trade={trade}
              leg={leg}
              legNumber={index + 1}
              editable={editable}
              isOnlyLeg={trade.legs.length === 1}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

interface LegMetrics {
  filled: number;
  pct: number;
  entry: number | null;
  exit: number | null;
  realized: string;
}

// Derive a leg's display roll-up from its own fills. Entry/exit are the
// quantity-weighted averages of the opening / closing executions. Realized is
// the exact sum of the leg's per-lot server-computed realized P&L (each lot's
// realized_pnl_amount is already net of its fee share, per the domain aggregate)
// -- summed in integer minor units so it never drifts. An open leg with no
// closes realizes nothing -> "$0.00"; a partially-closed leg shows only its
// closed-lot realized. This replaces a client-side float formula that counted a
// still-open leg's full entry cost as a realized loss.
function legMetrics(
  trade: components["schemas"]["TradeResponse"],
  leg: LegResponse,
): LegMetrics {
  const opening = leg.direction === "long" ? "bto" : "sto";
  const closing = leg.direction === "long" ? "stc" : "btc";
  let openQty = 0;
  let openValue = 0;
  let closeQty = 0;
  let closeValue = 0;
  for (const fill of leg.fills) {
    const qty = Number(fill.quantity);
    const value = qty * Number(fill.price_amount);
    if (fill.action === opening) {
      openQty += qty;
      openValue += value;
    }
    if (fill.action === closing) {
      closeQty += qty;
      closeValue += value;
    }
  }
  const target = Number(leg.target_qty);
  const realized =
    trade.legs.length === 1
      ? trade.realized_pnl_amount
      : sumDecimals(leg.lots.map((lot) => lot.realized_pnl_amount));
  return {
    filled: openQty,
    pct: target > 0 ? (openQty / target) * 100 : 0,
    entry: openQty > 0 ? openValue / openQty : null,
    exit: closeQty > 0 ? closeValue / closeQty : null,
    realized,
  };
}

function LegRow({
  trade,
  leg,
  legNumber,
  editable,
  isOnlyLeg,
}: {
  trade: components["schemas"]["TradeResponse"];
  leg: LegResponse;
  legNumber: number;
  editable: boolean;
  isOnlyLeg: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const deleteLeg = useDeleteTradeLeg();
  const { currency } = trade;
  const metrics = legMetrics(trade, leg);
  const realizedClass = pnlClassName(metrics.realized);
  const avgPrice =
    metrics.entry === null
      ? EM_DASH
      : metrics.exit === null
        ? formatPrice(metrics.entry.toFixed(4), { currency })
        : `${formatPrice(metrics.entry.toFixed(4), { currency })} → ${formatPrice(metrics.exit.toFixed(4), { currency })}`;

  return (
    <div className="overflow-hidden rounded-control border border-outline-variant">
      <div className="flex items-center gap-2 border-b border-outline-variant bg-surface-container-low px-3.5 py-2.5">
        <span className="text-[12.5px] font-semibold text-on-surface">
          Leg {legNumber}
        </span>
        <DirectionBadge direction={leg.direction} />
        {leg.is_flat ? (
          <span className="ml-auto rounded-full border border-outline-variant bg-surface px-2 py-0.5 text-[10px] font-medium text-on-surface-muted">
            Flat
          </span>
        ) : (
          <span className="ml-auto rounded-full border border-outline-variant bg-surface px-2 py-0.5 text-[10px] font-medium text-on-surface-muted">
            {formatQuantity(leg.open_qty)} open
          </span>
        )}
      </div>
      <div className="grid grid-cols-2">
        <LegFact label="Filled">
          {formatQuantity(metrics.filled.toString())} / {formatQuantity(leg.target_qty)}{" "}
          <span className="text-on-surface-muted">{Math.round(metrics.pct)}%</span>
        </LegFact>
        <LegFact label="Fills">{leg.fills.length}</LegFact>
        <LegFact label="Avg price">{avgPrice}</LegFact>
        <LegFact label="Realized" className={realizedClass}>
          {formatSignedMoney(metrics.realized, { currency })}
        </LegFact>
      </div>

      {editable ? (
        <div className="border-t border-outline-variant px-3.5 py-2.5">
          {confirming ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-on-surface-variant">Delete this leg?</span>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={deleteLeg.isPending}
                onClick={() => {
                  deleteLeg.mutate(
                    { tradeId: trade.trade_id, legId: leg.leg_id },
                    { onSettled: () => setConfirming(false) },
                  );
                }}
              >
                Confirm
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isOnlyLeg}
              title={
                isOnlyLeg
                  ? "A trade must keep at least one leg. Delete the whole trade instead."
                  : undefined
              }
              onClick={() => setConfirming(true)}
            >
              Delete leg
            </Button>
          )}
          {deleteLeg.isError ? (
            <p className="mt-2 text-sm text-danger" role="alert">
              {deleteLeg.error.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LegFact({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-outline-variant px-3.5 py-2.5 even:border-l even:border-outline-variant">
      <span className="text-[10.5px] font-medium text-on-surface-muted">{label}</span>
      <span className={`font-data text-[12.5px] font-semibold tabular-nums text-on-surface ${className ?? ""}`}>
        {children}
      </span>
    </div>
  );
}

function AddLegForm({ tradeId }: { tradeId: string }) {
  const addLeg = useAddLeg();
  const [instrumentId, setInstrumentId] = useState("");
  const [direction, setDirection] = useState<LegDirection>("long");
  const [targetQty, setTargetQty] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Add a leg</CardTitle>
        <CardDescription>Append a new directional leg to this trade.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            addLeg.mutate(
              {
                tradeId,
                body: {
                  instrument_id: instrumentId,
                  direction,
                  target_qty: targetQty,
                },
              },
              {
                onSuccess: () => {
                  setInstrumentId("");
                  setTargetQty("");
                  setDirection("long");
                },
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="add-leg-instrument">Instrument ID</Label>
            <Input
              id="add-leg-instrument"
              value={instrumentId}
              onChange={(event) => setInstrumentId(event.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="add-leg-direction">Direction</Label>
            <select
              id="add-leg-direction"
              className="h-9 w-full rounded-control border border-outline bg-surface-container px-3 text-sm text-on-surface"
              value={direction}
              onChange={(event) => setDirection(event.target.value as LegDirection)}
            >
              {DIRECTIONS.map((value) => (
                <option key={value} value={value}>
                  {value.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="add-leg-qty">Target quantity</Label>
            <Input
              id="add-leg-qty"
              type="number"
              step="any"
              min="0"
              value={targetQty}
              onChange={(event) => setTargetQty(event.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={addLeg.isPending}>
            Add leg
          </Button>
          {addLeg.isError ? (
            <p className="text-sm text-danger" role="alert">
              {addLeg.error.message}
            </p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function RecordFillForm({ tradeId, legs }: { tradeId: string; legs: LegResponse[] }) {
  const recordFill = useRecordFill();
  const firstLegId = legs[0]?.leg_id ?? "";
  const [legId, setLegId] = useState(firstLegId);
  const [action, setAction] = useState<FillAction>("bto");
  const [quantity, setQuantity] = useState("");
  const [priceAmount, setPriceAmount] = useState("");
  const [feesAmount, setFeesAmount] = useState("0");
  const [executedAt, setExecutedAt] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Record a fill</CardTitle>
        <CardDescription>Add one execution to a leg (FIFO close-out).</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            recordFill.mutate(
              {
                tradeId,
                body: {
                  leg_id: legId,
                  action,
                  quantity,
                  price_amount: priceAmount,
                  fees_amount: feesAmount,
                  executed_at: new Date(executedAt).toISOString(),
                },
              },
              {
                onSuccess: () => {
                  setQuantity("");
                  setPriceAmount("");
                  setFeesAmount("0");
                  setExecutedAt("");
                },
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="record-fill-leg">Leg</Label>
            <select
              id="record-fill-leg"
              className="h-9 w-full rounded-control border border-outline bg-surface-container px-3 text-sm text-on-surface"
              value={legId}
              onChange={(event) => setLegId(event.target.value)}
            >
              {legs.map((leg, index) => (
                <option key={leg.leg_id} value={leg.leg_id}>
                  Leg {index + 1} · {titleCase(leg.direction)} {leg.target_qty}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="record-fill-action">Action</Label>
            <select
              id="record-fill-action"
              className="h-9 w-full rounded-control border border-outline bg-surface-container px-3 text-sm text-on-surface"
              value={action}
              onChange={(event) => setAction(event.target.value as FillAction)}
            >
              {FILL_ACTIONS.map((value) => (
                <option key={value} value={value}>
                  {FILL_ACTION_EXECUTION_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="record-fill-qty">Quantity</Label>
            <Input
              id="record-fill-qty"
              type="number"
              step="any"
              min="0"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="record-fill-price">Price</Label>
            <Input
              id="record-fill-price"
              type="number"
              step="any"
              value={priceAmount}
              onChange={(event) => setPriceAmount(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="record-fill-fees">Fees</Label>
            <Input
              id="record-fill-fees"
              type="number"
              step="any"
              value={feesAmount}
              onChange={(event) => setFeesAmount(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="record-fill-executed-at">Executed at</Label>
            <Input
              id="record-fill-executed-at"
              type="datetime-local"
              value={executedAt}
              onChange={(event) => setExecutedAt(event.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={recordFill.isPending}>
            Record fill
          </Button>
          {recordFill.isError ? (
            <p className="text-sm text-danger" role="alert">
              {recordFill.error.message}
            </p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
